import {
  FormattingFilter,
  ArdoqId,
  APIScopeData,
  APIOrganizationUser,
} from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  enhancedScopeDataOperations,
  scopeDataOperations,
} from '@ardoq/scope-data';
import { isTruthy } from '@ardoq/common-helpers';
import { GraphInterface } from '@ardoq/graph';

export const couldBeArdoqId = (value: unknown): value is ArdoqId =>
  typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value);

/**
 * Extracts all potential entity IDs (components, workspaces, fields) from filter values.
 */
const extractPotentialEntityIdsFromFilters = (
  filters: FormattingFilter[]
): Set<string> =>
  new Set<string>(
    filters
      .map(({ value }) => (couldBeArdoqId(value) ? value : null))
      .filter(isTruthy)
  );

/**
 * Builds a minimal EnhancedScopeData containing only the entities referenced in the filters.
 * This optimization avoids building scope data for the entire app state when only a few
 * entities are needed to resolve entity names in conditional formatting legends.
 */
export const buildMinimalScopeDataForFilters = (
  graphInterface: GraphInterface,
  filters: FormattingFilter[],
  users: APIOrganizationUser[] = []
): EnhancedScopeData => {
  if (!filters.length) {
    return enhancedScopeDataOperations.getEmpty();
  }

  const potentialEntityIds = extractPotentialEntityIdsFromFilters(filters);

  // Collect components using graphInterface.get
  const components = Array.from(potentialEntityIds)
    .map(id => graphInterface.get(id))
    .filter(isTruthy);

  // Collect unique workspace IDs from potential IDs and components
  const workspaceIds = new Set<ArdoqId>([
    ...potentialEntityIds,
    ...components.map(c => c.rootWorkspace),
  ]);

  // Collect workspaces using graphInterface
  const workspaces = Array.from(workspaceIds)
    .map(id => graphInterface.getWorkspaceById(id))
    .filter(isTruthy);

  // Get unique model IDs from workspaces
  const modelIds = new Set(workspaces.map(ws => ws.componentModel));

  // Collect models from workspace attributes
  const models = Array.from(modelIds)
    .map(modelId => graphInterface.getModelData(modelId))
    .filter(isTruthy);

  // Collect fields that might be referenced by ID in filter values
  const fieldsByIds = Array.from(potentialEntityIds)
    .map(id => graphInterface.getFieldData(id))
    .filter(isTruthy);

  // Collect fields referenced by name in filter.name property
  const fieldNames = new Set(
    filters.map(filter => filter.name).filter(isTruthy)
  );
  const fieldsByNames = Array.from(fieldNames)
    .map(name => graphInterface.getFieldByName(name))
    .filter(isTruthy);

  // Combine both sources of fields
  const fields = [...fieldsByIds, ...fieldsByNames];

  // Filter users to only include those referenced in the filters
  const relevantUsers = users.filter(user => potentialEntityIds.has(user._id));

  const minimalScopeData: APIScopeData = {
    components,
    workspaces,
    models,
    fields,
    references: [],
    tags: [],
    attachments: [],
    scopeComponents: [],
    dateRangeFieldMap: new Map(),
    users: [],
    organizationUsers: relevantUsers,
  };

  return scopeDataOperations.enhance(minimalScopeData);
};
